import type { WorkspaceScreen } from "../core/navigation.js";
import { dispatchAppBack, showMobileFeedback } from "./app-feedback.js";

const BACK_EXIT_WINDOW_MS = 2_250;
const HISTORY_KEY = "chamsPayWorkspace";
const HISTORY_ROOT = "root";
const HISTORY_GUARD = "guard";
let historyPrepared = false;

export interface AppHistoryState {
  readonly handheld: boolean;
  readonly screen: WorkspaceScreen;
  readonly studentDetail: boolean;
  readonly returnFromStudent: () => void;
  readonly showDashboard: () => void;
}

interface PlatformCloseWatcher {
  onclose: ((event: Event) => void) | null;
  destroy: () => void;
}

type PlatformCloseWatcherConstructor = new () => PlatformCloseWatcher;

function installedAsApp(): boolean {
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    (navigator as Navigator & { readonly standalone?: boolean }).standalone === true
  );
}

function handleInternalBack(current: AppHistoryState): boolean {
  if (dispatchAppBack()) return true;
  if (current.studentDetail) {
    current.returnFromStudent();
    return true;
  }
  if (current.screen !== "dashboard") {
    current.showDashboard();
    return true;
  }
  return false;
}

function installPlatformCloseBack(
  state: () => AppHistoryState,
  CloseWatcher: PlatformCloseWatcherConstructor,
): () => void {
  let watcher: PlatformCloseWatcher | null = null;
  let exitTimer: number | null = null;
  let exitArmed = false;
  let disposed = false;

  const arm = () => {
    if (disposed || exitArmed || watcher) return;
    watcher = new CloseWatcher();
    watcher.onclose = () => {
      watcher = null;
      const current = state();
      if (handleInternalBack(current)) {
        window.queueMicrotask(arm);
        return;
      }
      exitArmed = true;
      showMobileFeedback("back-to-exit");
      exitTimer = window.setTimeout(() => {
        exitTimer = null;
        exitArmed = false;
        arm();
      }, BACK_EXIT_WINDOW_MS);
    };
  };
  const resetExitIntent = () => {
    if (!exitArmed) return;
    exitArmed = false;
    if (exitTimer !== null) window.clearTimeout(exitTimer);
    exitTimer = null;
    arm();
  };

  arm();
  window.addEventListener("pointerdown", resetExitIntent, true);
  window.addEventListener("keydown", resetExitIntent, true);
  return () => {
    disposed = true;
    watcher?.destroy();
    if (exitTimer !== null) window.clearTimeout(exitTimer);
    window.removeEventListener("pointerdown", resetExitIntent, true);
    window.removeEventListener("keydown", resetExitIntent, true);
  };
}

/**
 * Keeps one same-document history guard in front of the workspace. Back first
 * dismisses an overlay, then student detail, then a main screen; only a second
 * Back from the dashboard leaves the application.
 */
export function installAppHistory(state: () => AppHistoryState): () => void {
  const CloseWatcher = (
    globalThis as typeof globalThis & {
      readonly CloseWatcher?: PlatformCloseWatcherConstructor;
    }
  ).CloseWatcher;
  const current = state();
  if (current.handheld && installedAsApp() && CloseWatcher)
    return installPlatformCloseBack(state, CloseWatcher);

  let lastExitBackAt = 0;
  const marker = () => (history.state as Record<string, unknown> | null)?.[HISTORY_KEY];
  const arm = () => {
    if (marker() !== HISTORY_GUARD)
      history.pushState({ ...history.state, [HISTORY_KEY]: HISTORY_GUARD }, "", location.href);
  };
  if (!historyPrepared) {
    history.replaceState({ ...history.state, [HISTORY_KEY]: HISTORY_ROOT }, "", location.href);
    arm();
    historyPrepared = true;
  } else arm();

  const onPopState = () => {
    const current = state();
    if (handleInternalBack(current)) {
      lastExitBackAt = 0;
      arm();
      return;
    }
    const now = Date.now();
    if (now - lastExitBackAt <= BACK_EXIT_WINDOW_MS) {
      history.back();
      return;
    }
    lastExitBackAt = now;
    if (current.handheld) showMobileFeedback("back-to-exit");
    arm();
  };

  window.addEventListener("popstate", onPopState);
  return () => window.removeEventListener("popstate", onPopState);
}
